import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import LabMindMockup from "@/components/landing/LabMindMockup";
import AgentFlowMockup from "@/components/landing/AgentFlowMockup";
import AIRouterMockup from "@/components/landing/AIRouterMockup";

const MOCKUPS = [LabMindMockup, AgentFlowMockup, AIRouterMockup];

const CaseStudies = () => {
  const { t } = useLang();
  const scrollLead = () => {
    const el = document.getElementById("lead");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="cases" data-testid="case-studies-section" className="py-16 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <div className="eyebrow">{t.cases.eyebrow}</div>
          <h2 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tight text-maif">
            {t.cases.title}
          </h2>
        </div>

        <div className="space-y-20 md:space-y-28">
          {t.cases.items.map((it, i) => {
            const Mock = MOCKUPS[i] || LabMindMockup;
            const firstWord = it.title.split(" ")[0];
            return (
              <article
                key={i}
                data-testid={`case-study-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7 relative w-full">
                  <div className="mb-4">
                    <span className="inline-flex items-center rounded-full border border-sand-mid bg-maif-2 px-3 py-1 text-xs font-medium text-sand/90">
                      {it.tag}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={scrollLead}
                    data-testid={`case-mockup-${i}`}
                    aria-label={`Discuss ${it.title}`}
                    className="group relative w-full text-left block rounded-2xl overflow-hidden transition-all duration-500 ease-[cubic-bezier(.2,.8,.2,1)] hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.6)] active:translate-y-0 active:scale-[0.995] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0B]"
                  >
                    <div className="absolute inset-0 rounded-2xl border border-white/10 group-hover:border-terracotta/40 transition-colors duration-500 pointer-events-none"></div>
                    
                    <div className="relative w-full aspect-[16/10] min-h-[480px] sm:min-h-[440px]">
                      <Mock />
                    </div>
                    
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-end px-5 pb-4 pt-12 bg-gradient-to-t from-[#0A0A0B]/90 via-[#0A0A0B]/30 to-transparent opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-300">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-terracotta">
                        Discuss {firstWord}
                        <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </button>
                </div>

                <div className="lg:col-span-5">
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight text-maif">
                    {it.title}
                  </h3>

                  <div className="mt-6 space-y-5">
                    <div>
                      <div className="eyebrow text-maif-muted/90">Problem</div>
                      <p className="mt-2 text-maif-muted/90">{it.problem}</p>
                    </div>
                    <div>
                      <div className="eyebrow text-terracotta">Solution</div>
                      <p className="mt-2 text-maif">{it.solution}</p>
                    </div>
                  </div>

                  <div className="mt-8 flex items-baseline gap-3 border-t border-sand-soft pt-6">
                    <span className="font-display font-light text-5xl md:text-6xl text-terracotta tracking-tighter">
                      {it.metric}
                    </span>
                    <span className="text-maif-muted/90 text-sm">{it.metricLabel}</span>
                  </div>

                  <button
                    type="button"
                    onClick={scrollLead}
                    data-testid={`case-cta-${i}`}
                    className="mt-8 inline-flex items-center gap-2 text-terracotta hover:text-[#BD5F3F] text-sm font-medium transition-all duration-200 group hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Discuss {firstWord} for your team
                    <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
